<template>
  <div class="progress-bar-widget" :style="widgetStyles">
    <div v-if="widget.title" class="progress-title mb-4">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>

    <div class="progress-bars-container">
      <div 
        v-for="(bar, index) in widget.bars" 
        :key="bar.id"
        class="progress-item mb-3"
      >
        <!-- Progress Label -->
        <div v-if="widget.showLabels !== false" class="progress-label mb-2 d-flex justify-content-between align-items-center">
          <span class="progress-label-text">{{ bar.label }}</span>
          <span v-if="bar.showPercentage !== false" class="progress-percentage">{{ animatedPercentages[index] || 0 }}%</span>
        </div>

        <!-- Progress Bar -->
        <div class="progress progress-bar-container" :style="progressContainerStyles">
          <div 
            class="progress-bar"
            :class="progressBarClasses(bar)"
            role="progressbar"
            :style="progressBarStyles(bar, index)"
            :aria-valuenow="bar.percentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <span v-if="bar.showPercentage === false && widget.showLabels === false" class="progress-text">
              {{ bar.percentage }}%
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import type { ProgressBarWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'

interface Props {
  widget: ProgressBarWidget
}

const props = defineProps<Props>()

// Animation state
const isAnimated = ref(false)
const animatedPercentages = ref<number[]>([])

// Animate bars on mount
onMounted(() => {
  // Initialize animated percentages to 0
  animatedPercentages.value = props.widget.bars.map(() => 0)
  
  if (props.widget.animated !== false) {
    nextTick(() => {
      setTimeout(() => {
        isAnimated.value = true
        animateNumbers()
      }, 200)
    })
  } else {
    isAnimated.value = true
    animatedPercentages.value = props.widget.bars.map(bar => bar.percentage)
  }
})

const animateNumbers = () => {
  const duration = 1500
  const steps = 60
  const stepDuration = duration / steps
  
  props.widget.bars.forEach((bar, index) => {
    let currentStep = 0
    const increment = bar.percentage / steps
    
    const interval = setInterval(() => {
      currentStep++
      if (currentStep >= steps) {
        animatedPercentages.value[index] = bar.percentage
        clearInterval(interval)
      } else {
        animatedPercentages.value[index] = Math.floor(increment * currentStep)
      }
    }, stepDuration)
  })
}

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '1rem 0',
  ...resolveWidgetBackgroundStyle(props.widget),
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || '#333',
  fontSize: props.widget.titleSize || '1.5rem',
  textAlign: props.widget.titleAlignment || 'left',
  margin: '0'
}))

const progressContainerStyles = computed(() => ({
  height: props.widget.height || '20px',
  backgroundColor: '#e9ecef',
  borderRadius: '10px',
  overflow: 'hidden'
}))

// Methods
const progressBarClasses = (bar: any) => {
  const style = props.widget.style || 'default'
  return {
    'progress-bar-striped': style === 'striped' || (style === 'default' && props.widget.animated),
    'progress-bar-animated': props.widget.animated !== false && isAnimated.value
  }
}

const progressBarStyles = (bar: any, index: number) => {
  const defaultColors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#6c757d']
  const color = bar.color || defaultColors[index % defaultColors.length]
  
  let background = color
  
  if (props.widget.style === 'gradient') {
    // Create a gradient effect
    const lighterColor = lightenColor(color, 20)
    background = `linear-gradient(45deg, ${color} 25%, ${lighterColor} 25%, ${lighterColor} 50%, ${color} 50%, ${color} 75%, ${lighterColor} 75%)`
  }
  
  return {
    width: isAnimated.value ? `${bar.percentage}%` : '0%',
    backgroundColor: props.widget.style !== 'gradient' ? color : undefined,
    background: props.widget.style === 'gradient' ? background : undefined,
    transition: props.widget.animated !== false ? 'width 1.5s ease-in-out' : 'none',
    borderRadius: '10px'
  }
}

// Helper function to lighten color
const lightenColor = (color: string, percent: number) => {
  const num = parseInt(color.replace("#", ""), 16)
  const amt = Math.round(2.55 * percent)
  const R = (num >> 16) + amt
  const B = (num >> 8 & 0x00FF) + amt
  const G = (num & 0x0000FF) + amt
  return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (B < 255 ? B < 1 ? 0 : B : 255) * 0x100 + (G < 255 ? G < 1 ? 0 : G : 255)).toString(16).slice(1)
}
</script>

<style scoped>
.progress-bar-widget {
  max-width: 100%;
}

.progress-title h3 {
  margin: 0;
  font-weight: 600;
}

.progress-item {
  margin-bottom: 1.5rem;
}

.progress-item:last-child {
  margin-bottom: 0;
}

.progress-label {
  font-weight: 500;
  color: #495057;
  font-size: 0.9rem;
}

.progress-label-text {
  font-weight: 600;
}

.progress-percentage {
  font-weight: 600;
  color: #6c757d;
  font-size: 0.85rem;
}

.progress-bar-container {
  background-color: #e9ecef;
  border-radius: 10px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.progress-bar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  transition: width 0.6s ease;
  border-radius: inherit;
  font-weight: 600;
  font-size: 0.75rem;
  position: relative;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-weight: 600;
  font-size: 0.75rem;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

/* Striped Animation */
.progress-bar-striped {
  background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent);
  background-size: 1rem 1rem;
}

.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}

.progress-bar {
  animation: pulse-glow 2s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.4);
  }
}

@keyframes progress-bar-stripes {
  0% {
    background-position-x: 1rem;
  }
}

/* Hover Effects */
.progress-item:hover .progress-bar {
  filter: brightness(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Different Styles */
.progress-bar-widget[data-style="minimal"] .progress-bar-container {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
}

.progress-bar-widget[data-style="rounded"] .progress-bar-container,
.progress-bar-widget[data-style="rounded"] .progress-bar {
  border-radius: 50px;
}

.progress-bar-widget[data-style="square"] .progress-bar-container,
.progress-bar-widget[data-style="square"] .progress-bar {
  border-radius: 2px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .progress-label {
    font-size: 0.8rem;
  }
  
  .progress-percentage {
    font-size: 0.75rem;
  }
  
  .progress-item {
    margin-bottom: 1rem;
  }
}

@media (max-width: 576px) {
  .progress-label {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.25rem;
  }
  
  .progress-percentage {
    font-size: 0.8rem;
    color: #007bff;
    font-weight: 700;
  }
}

/* Accessibility */
.progress-bar[aria-valuenow] {
  position: relative;
}

.progress-bar::before {
  content: attr(aria-valuenow) '%';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0;
  opacity: 0;
  transition: all 0.3s ease;
}

.progress-item:focus-within .progress-bar::before {
  font-size: 0.75rem;
  opacity: 1;
}

/* Custom Heights */
.progress-bar-widget[data-height="small"] .progress-bar-container {
  height: 12px !important;
}

.progress-bar-widget[data-height="medium"] .progress-bar-container {
  height: 20px !important;
}

.progress-bar-widget[data-height="large"] .progress-bar-container {
  height: 30px !important;
}

.progress-bar-widget[data-height="extra-large"] .progress-bar-container {
  height: 40px !important;
}
</style>